import { createPlan } from '@/api/generated/flow/flow';
import { createGoal } from '@/api/generated/goals/goals';
import type { GoalPlanningInterestId } from '@/components/goalPlanning/goalPlanningConstants';
import { assumptionsFromFinancialInfo } from '@/features/planning/incomeSources';
import {
  goalPlanningStorage,
  type FinancialInfo,
  type GoalPlan,
  type PlannedGoal,
} from '@/services/goalPlanningStorage';
import axios from 'axios';

function isServerError(error: unknown): boolean {
  return axios.isAxiosError(error) && (error.response?.status ?? 0) >= 500;
}

function toDueDate(isoOrDate: string): string {
  return isoOrDate.includes('T') ? isoOrDate.split('T')[0]! : isoOrDate;
}

export async function persistOnboardingGoals(params: {
  goals: PlannedGoal[];
  interests: GoalPlanningInterestId[];
  defaultAccountId: string | undefined;
  financialInfo?: FinancialInfo | null;
}): Promise<void> {
  const { goals, interests, defaultAccountId, financialInfo } = params;

  if (!defaultAccountId) {
    throw new Error('No asset account available to link goals');
  }

  try {
    for (const goal of goals) {
      await createGoal({
        name: goal.name,
        description: goal.description,
        accountId: defaultAccountId,
        targetAmount: goal.targetAmount,
        currentAmount: goal.currentAmount ?? 0,
        currency: goal.currency,
        dueDate: toDueDate(goal.dueDate),
        priority: goal.priority,
        category: 'lifestyle',
        active: true,
        goalType: goal.goalType,
        templateParams: goal.templateParams,
      });
    }

    if (financialInfo) {
      const incomeAssumptions = assumptionsFromFinancialInfo(financialInfo);
      await createPlan({
        name: `${new Date().getFullYear()} plan`,
        horizon: 'tenYear',
        isDefault: true,
        assumptions: {
          residenceCountry: 'GB',
          filingStatus: 'single',
          monthlyLivingExpenses: 0,
          expensesAnnualGrowthRate: 0.03,
          numberOfDependents: 0,
          effectiveTaxRate: 0.22,
          accountGrowthRates: [],
          ...incomeAssumptions,
        },
      });
    }

    goalPlanningStorage.saveInterests(interests);
    goalPlanningStorage.markCompleted();
  } catch (error) {
    if (!isServerError(error)) {
      throw error;
    }
    const plan: GoalPlan = {
      goals,
      interests,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    };
    goalPlanningStorage.saveGoalPlan(plan);
    goalPlanningStorage.saveInterests(interests);
    goalPlanningStorage.markCompleted();
  }
}
